/**
 * Authentication Service for Admin Web
 * Uses Supabase Auth (browser-compatible)
 */

import { supabase } from './supabase'

export interface LoginCredentials {
  email: string
  password: string
  rememberMe?: boolean
}

export interface AdminUser {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  adminLevel: 'super_admin' | 'church_owner' | 'church_admin' | 'church_elder' | 'ministry_leader' | 'none'
  churchId?: string
  phone?: string
  twoFactorEnabled: boolean
  twoFactorMethod?: 'sms' | 'authenticator'
}

export interface AuthResponse {
  success: boolean
  user?: AdminUser
  token?: string
  requiresTwoFactor?: boolean
  twoFactorToken?: string
  error?: string
}

/**
 * Login with email and password using Supabase Auth
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  const { email, password } = credentials

  console.log('🔐 [LOGIN STEP 1] Starting login for:', email)

  try {
    // Sign in with Supabase Auth
    console.log('🔐 [LOGIN STEP 2] Calling Supabase auth...')
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    console.log('🔐 [LOGIN STEP 3] Supabase auth response:', {
      hasUser: !!authData?.user,
      hasSession: !!authData?.session,
      error: authError?.message
    })

    if (authError || !authData.user) {
      console.error('Supabase Auth error:', authError)

      // Try to log failed attempt (non-blocking, may fail if user doesn't exist)
      try {
        await supabase.from('audit_logs').insert({
          action: 'login_failed',
          status: 'failure',
          details: { email, reason: authError?.message || 'unknown' }
        })
      } catch (logError) {
        console.warn('Failed to log audit:', logError)
      }

      return { success: false, error: authError?.message || 'Invalid email or password' }
    }

    // Get profile data
    console.log('🔐 [LOGIN STEP 4] Fetching user profile for ID:', authData.user.id)
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', authData.user.id)
      .single()

    console.log('🔐 [LOGIN STEP 5] Profile result:', {
      hasProfile: !!profile,
      role: profile?.role,
      churchId: profile?.church_id,
      error: profileError?.message
    })

    if (profileError || !profile) {
      console.error('🔐 [LOGIN ERROR] Profile not found')
      return { success: false, error: 'Profile not found' }
    }

    // Check if user is admin
    console.log('🔐 [LOGIN STEP 6] Checking admin role...')
    if (profile.role !== 'admin') {
      await supabase.auth.signOut()
      return { success: false, error: 'Access denied. Admin access required.' }
    }

    // Check if account is locked
    if (profile.account_locked_until) {
      const lockUntil = new Date(profile.account_locked_until)
      if (lockUntil > new Date()) {
        const minutesLeft = Math.ceil((lockUntil.getTime() - Date.now()) / 60000)
        await supabase.auth.signOut()
        return {
          success: false,
          error: `Account locked. Try again in ${minutesLeft} minutes.`
        }
      }
    }

    // Reset failed login attempts on successful login
    await supabase
      .from('profiles')
      .update({ failed_login_attempts: 0, account_locked_until: null })
      .eq('id', authData.user.id)

    // Check if 2FA is enabled
    console.log('Profile 2FA check:', {
      two_factor_enabled: profile.two_factor_enabled,
      two_factor_method: profile.two_factor_method,
      phone: profile.phone
    })

    if (profile.two_factor_enabled) {
      console.log('2FA is enabled - sending to 2FA page')
      // Store temp session data for 2FA verification
      const tempToken = authData.session?.access_token || ''

      return {
        success: true,
        requiresTwoFactor: true,
        twoFactorToken: tempToken
      }
    }

    console.log('🔐 [LOGIN STEP 7] 2FA is NOT enabled - proceeding to dashboard')

    // Log successful login (non-blocking)
    console.log('🔐 [LOGIN STEP 8] Logging audit entry...')
    try {
      await supabase.from('audit_logs').insert({
        user_id: authData.user.id,
        church_id: profile.church_id || null,
        action: 'login',
        status: 'success',
        details: { method: 'password' }
      })
      console.log('🔐 [LOGIN STEP 9] Audit log successful')
    } catch (auditError) {
      console.warn('🔐 [LOGIN STEP 9] Failed to log audit entry (non-blocking):', auditError)
    }

    console.log('🔐 [LOGIN STEP 10] Creating admin user object...')
    const adminUser: AdminUser = {
      id: profile.id,
      email: profile.email,
      firstName: profile.first_name,
      lastName: profile.last_name,
      role: profile.role,
      adminLevel: profile.admin_level || 'none',
      churchId: profile.church_id,
      phone: profile.phone,
      twoFactorEnabled: profile.two_factor_enabled || false,
      twoFactorMethod: profile.two_factor_method
    }

    console.log('🔐 [LOGIN STEP 11] Returning success with user:', {
      id: adminUser.id,
      email: adminUser.email,
      role: adminUser.role,
      churchId: adminUser.churchId
    })

    return {
      success: true,
      user: adminUser,
      token: authData.session?.access_token
    }
  } catch (error: any) {
    console.error('🔐 [LOGIN ERROR] Caught exception:', error)
    return { success: false, error: 'An error occurred during login' }
  }
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()

    if (user) {
      // Log logout (non-blocking)
      try {
        await supabase.from('audit_logs').insert({
          user_id: user.id,
          action: 'logout',
          status: 'success'
        })
      } catch (auditError) {
        console.warn('Failed to log audit entry:', auditError)
      }
    }

    await supabase.auth.signOut()
  } catch (error) {
    console.error('Logout error:', error)
  }
}

/**
 * Get current user session
 */
export async function validateSession(): Promise<AdminUser | null> {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession()

    if (sessionError || !session) {
      return null
    }

    // Get profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .single()

    if (profileError || !profile) {
      return null
    }

    // Check if admin
    if (profile.role !== 'admin') {
      return null
    }

    return {
      id: profile.id,
      email: profile.email,
      firstName: profile.first_name,
      lastName: profile.last_name,
      role: profile.role,
      adminLevel: profile.admin_level || 'none',
      churchId: profile.church_id,
      phone: profile.phone,
      twoFactorEnabled: profile.two_factor_enabled || false,
      twoFactorMethod: profile.two_factor_method
    }
  } catch (error) {
    console.error('Session validation error:', error)
    return null
  }
}

/**
 * Request password reset
 * Uses Supabase Auth built-in password reset with custom SMTP
 */
export async function requestPasswordReset(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('🔍 DEBUG: Starting password reset request for:', email)

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })

    if (error) {
      console.error('❌ Password reset error:', error)
      return { success: false, error: 'Failed to send password reset email. Please try again.' }
    }

    console.log('✅ Password reset email sent successfully via Supabase Auth')
    return { success: true }

  } catch (error: any) {
    console.error('❌ Caught exception:', error)
    return { success: false, error: 'Failed to request password reset' }
  }
}

/**
 * Update password
 */
export async function updatePassword(newPassword: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    })

    if (error) {
      return { success: false, error: error.message }
    }

    // Log password change (non-blocking)
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      try {
        await supabase.from('audit_logs').insert({
          user_id: user.id,
          action: 'password_changed',
          status: 'success'
        })
      } catch (auditError) {
        console.warn('Failed to log audit entry:', auditError)
      }
    }

    return { success: true }
  } catch (error) {
    console.error('Password update error:', error)
    return { success: false, error: 'Failed to update password' }
  }
}
